import { ProfileHeader, ProfileHeaderOwnProps, ProfileNoteHeader } from './ProfileHeader';
import LogoHeader, { LogoHeaderOwnProps } from './LogoHeader';
import StandardHeader, { StandardHeaderOwnProps } from './StandardHeader';
import BackDisabledStandardHeader, { BackDisabledStandardHeaderOwnProps } from './BackDisabledStandardHeader';
import { NativeStackHeaderProps } from '@react-navigation/native-stack';
import BreadCrumbs, { BreadCrumbPathPartType, BreadCrumbsOwnProps } from './Breadcrumbs';
import { useIsMobileLayout } from '../../../hooks/useIsMobileLayout';
import NoHeader, { NoHeaderOwnProps } from './NoHeader';
import DesktopLogoHeader, { DesktopLogoHeaderOwnProps } from './DektopLogoHeader';

type HeaderTypeAndProps<
    TProps,
    TComponent extends React.FC<TProps & NativeStackHeaderProps>,
> = keyof TProps extends never
    ? {
          header: TComponent;
      }
    : {
          props: TProps;
          header: TComponent;
      };

export type MobileHeaderTypeAndProps =
    | HeaderTypeAndProps<LogoHeaderOwnProps, typeof LogoHeader>
    | HeaderTypeAndProps<ProfileHeaderOwnProps, typeof ProfileHeader>
    | HeaderTypeAndProps<ProfileHeaderOwnProps, typeof ProfileNoteHeader>
    | HeaderTypeAndProps<StandardHeaderOwnProps, typeof StandardHeader>
    | HeaderTypeAndProps<BackDisabledStandardHeaderOwnProps, typeof BackDisabledStandardHeader>;

export const mobileHeaderConfigs = {
    logo: { header: LogoHeader },
    profile: { header: ProfileHeader },
    profileNoChatButton: {
        header: ProfileHeader,
        props: {
            omitChatButton: true,
        },
    },
    profileNote: { header: ProfileNoteHeader },
    profileNoteNoChatButton: {
        header: ProfileNoteHeader,
        props: {
            omitChatButton: true,
        },
    },
    standard: { header: StandardHeader },
    backDisabledStandard: { header: BackDisabledStandardHeader },
};

export const BreadCrumbsHeader = BreadCrumbs;

export type DesktopHeaderTypeAndProps =
    | HeaderTypeAndProps<BreadCrumbsOwnProps, typeof BreadCrumbs>
    | HeaderTypeAndProps<NoHeaderOwnProps, typeof NoHeader>
    | HeaderTypeAndProps<DesktopLogoHeaderOwnProps, typeof DesktopLogoHeader>;

export const desktopHeaderConfigs = {
    breadCrumbs: { header: BreadCrumbs },
    noHeader: { header: NoHeader },
    logo: { header: DesktopLogoHeader },
};

export type HeaderConfiguration = {
    mobile: MobileHeaderTypeAndProps;
} & (
    | {
          desktop?: DesktopHeaderTypeAndProps;
      }
    | {
          breadCrumbs?: BreadCrumbPathPartType[];
      }
);

const DeviceConditionalHeader: React.FC<NativeStackHeaderProps & HeaderConfiguration> = ({
    mobile,
    ...toPass
}: HeaderConfiguration) => {
    const isMobile = useIsMobileLayout();
    if (isMobile) {
        return <mobile.header {...toPass} {...('props' in mobile ? (mobile.props as any) : {})} />;
    } else if ('desktop' in toPass && toPass.desktop) {
        return (
            <toPass.desktop.header {...toPass} {...('props' in toPass.desktop ? (toPass.desktop.props as any) : {})} />
        );
    } else if ('breadCrumbs' in toPass && toPass.breadCrumbs) {
        return <BreadCrumbs path={toPass.breadCrumbs} {...(toPass as any)} />;
    } else {
        return <NoHeader {...(toPass as any)} />;
    }
};

export const getHeader = (configuration: HeaderConfiguration) => (props: NativeStackHeaderProps) => {
    return <DeviceConditionalHeader {...props} {...configuration} />;
};
